import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Eye, Edit, Download, MessageSquare } from 'lucide-react';
import axios from '../api/client';

const DocumentApprovals = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [approvalAction, setApprovalAction] = useState({
    action: 'approve',
    comments: ''
  });

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get('/sops/pending-approval/list');
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedDoc) return;

    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      await axios.post(`/sops/${selectedDoc.sop.id}/workflow/approve`, {
        stage: selectedDoc.workflow.current_stage,
        action: approvalAction.action,
        approver_id: currentUser.id || 'admin',
        approver_name: currentUser.name || 'Admin User',
        comments: approvalAction.comments
      });

      setShowApprovalModal(false);
      setApprovalAction({ action: 'approve', comments: '' });
      fetchPendingApprovals();
      alert(`Document ${approvalAction.action === 'approve' ? 'approved' : 'rejected'} successfully!`);
    } catch (error) {
      console.error('Error processing approval:', error);
      alert('Failed to process approval');
    }
  };

  const handleDownload = async (sopId, versionNumber) => {
    try {
      const response = await axios.get(`/sops/${sopId}/download/${versionNumber}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SOP_v${versionNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const getStageIcon = (stage) => {
    switch(stage) {
      case 'editor': return <Edit size={16} />;
      case 'departmental': return <FileText size={16} />;
      case 'qa': return <CheckCircle size={16} />;
      case 'director': return <FileText size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStageColor = (status) => {
    switch(status) {
      case 'approve': return 'text-green-600';
      case 'approved': return 'text-green-600';
      case 'reject': return 'text-red-600';
      case 'rejected': return 'text-red-600';
      case 'pending': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FileText className="mr-3" />
          Document Approval Workflow
        </h1>
        <p className="text-gray-600 mt-2">Review and approve documents in the approval cascade</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Pending Approvals</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingApprovals.length}</p>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Editor Stage</p>
              <p className="text-2xl font-bold text-blue-600">
                {pendingApprovals.filter(p => p.workflow.current_stage === 'editor').length}
              </p>
            </div>
            <Edit className="text-blue-500" size={32} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">QA Stage</p>
              <p className="text-2xl font-bold text-purple-600">
                {pendingApprovals.filter(p => p.workflow.current_stage === 'qa').length}
              </p>
            </div>
            <CheckCircle className="text-purple-500" size={32} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Director Stage</p>
              <p className="text-2xl font-bold text-green-600">
                {pendingApprovals.filter(p => p.workflow.current_stage === 'director').length}
              </p>
            </div>
            <FileText className="text-green-500" size={32} />
          </div>
        </div>
      </div>

      {/* Pending Documents */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Documents Awaiting Approval</h2>
        </div>
        
        {pendingApprovals.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="mx-auto mb-4 text-gray-400" size={48} />
            <p>No documents pending approval</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {pendingApprovals.map((item) => (
              <div key={item.workflow.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.sop.sop_number}: {item.sop.title}
                      </h3>
                      <span className="ml-3 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        Current: {item.workflow.current_stage.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                      <div><strong>Category:</strong> {item.sop.category}</div>
                      <div><strong>Department:</strong> {item.sop.department}</div>
                      <div><strong>Version:</strong> v{item.sop.current_version}</div>
                      <div><strong>ISO Clause:</strong> {item.sop.iso_clause || 'N/A'}</div>
                    </div>

                    {/* Workflow Progress */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Approval Progress:</p>
                      <div className="flex items-center space-x-2">
                        {item.workflow.stages.map((stage, idx) => (
                          <React.Fragment key={idx}>
                            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
                              stage.status === 'approved' || stage.status === 'approve' ? 'bg-green-50 border-green-200' :
                              stage.status === 'rejected' || stage.status === 'reject' ? 'bg-red-50 border-red-200' :
                              stage.stage === item.workflow.current_stage ? 'bg-yellow-50 border-yellow-200' :
                              'bg-gray-50 border-gray-200'
                            }`}>
                              {getStageIcon(stage.stage)}
                              <span className={`text-sm font-medium ${getStageColor(stage.status)}`}>
                                {stage.stage.charAt(0).toUpperCase() + stage.stage.slice(1)}
                              </span>
                            </div>
                            {idx < item.workflow.stages.length - 1 && (
                              <div className="flex-1 h-0.5 bg-gray-300"></div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="ml-4 flex flex-col space-y-2">
                    <button
                      onClick={() => handleDownload(item.sop.id, item.sop.current_version)}
                      className="flex items-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                    >
                      <Eye size={16} className="mr-1" />
                      View Document
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDoc(item);
                        setShowApprovalModal(true);
                      }}
                      className="flex items-center px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                    >
                      <CheckCircle size={16} className="mr-1" />
                      Review
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold mb-4">
              Review Document: {selectedDoc.sop.sop_number}
            </h2>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2"><strong>Title:</strong> {selectedDoc.sop.title}</p>
              <p className="text-gray-700 mb-2"><strong>Current Stage:</strong> {selectedDoc.workflow.current_stage}</p>
              <p className="text-gray-700 mb-4"><strong>Version:</strong> v{selectedDoc.sop.current_version}</p>
              
              {/* Show AI Analysis if available */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Note:</strong> AI analysis results are stored in the system. 
                  You can view them in the SOP Management section by clicking the AI Analysis button.
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="approval_action"
                    value="approve"
                    checked={approvalAction.action === 'approve'}
                    onChange={(e) => setApprovalAction({...approvalAction, action: e.target.value})}
                    className="mr-2"
                  />
                  <CheckCircle size={20} className="text-green-600 mr-1" />
                  <span>Approve</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="approval_action"
                    value="reject"
                    checked={approvalAction.action === 'reject'}
                    onChange={(e) => setApprovalAction({...approvalAction, action: e.target.value})}
                    className="mr-2"
                  />
                  <XCircle size={20} className="text-red-600 mr-1" />
                  <span>Reject</span>
                </label>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Comments</label>
              <textarea
                value={approvalAction.comments}
                onChange={(e) => setApprovalAction({...approvalAction, comments: e.target.value})}
                rows="4"
                placeholder="Add your review comments..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setApprovalAction({ action: 'approve', comments: '' });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className={`px-4 py-2 rounded-lg text-white ${
                  approvalAction.action === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {approvalAction.action === 'approve' ? 'Approve Document' : 'Reject Document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentApprovals;
